import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon,
  Upload, X, Save, ArrowLeft, Copy, Sparkles,
} from "lucide-react";

/**
 * Admin quiz builder — full CRUD.
 *
 * Two views:
 *   1. Quiz list (grouped by course + language) with "Edit" + "New" buttons.
 *   2. Editor: title/meta on top; questions below with:
 *        • prompt text
 *        • optional prompt image (below the prompt when rendered to students)
 *        • options — each can be text-only OR image-only
 *        • correct answer picker (radio)
 *        • per-question explanation
 *        • up/down reorder arrows + duplicate + delete
 *
 * All image fields go through /api/admin/uploads/image which returns a
 * URL served from /api/uploads/*.
 */
export default function AdminQuizzes() {
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // quiz object or "new"

  const load = async () => {
    setLoading(true);
    try {
      const [qr, cr] = await Promise.all([
        api.get("/admin/quizzes"),
        api.get("/admin/content/courses"),
      ]);
      setQuizzes(qr.data);
      setCourses(cr.data);
    } catch (e) {
      toast.error("Couldn't load quizzes.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Group by course_id + language for the list view. Must be declared
  // BEFORE any conditional return so React Hooks fire in a stable order.
  const grouped = useMemo(() => {
    const g = {};
    for (const q of quizzes) {
      const key = `${q.course_id}::${q.language}`;
      if (!g[key]) g[key] = [];
      g[key].push(q);
    }
    for (const key of Object.keys(g)) {
      g[key].sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0));
    }
    return g;
  }, [quizzes]);

  if (editing) {
    return (
      <QuizEditor
        initial={editing === "new" ? null : editing}
        courses={courses}
        quizzes={quizzes}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  const courseById = (courseId) => courses.find((c) => c.id === courseId);
  const courseTitle = (courseId) => courseById(courseId)?.title || courseId;

  return (
    <div data-testid="admin-quizzes-page" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-1">
            — Admin
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">
            Chapter quizzes
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-xl">
            Build multiple-choice quizzes with text or image prompts and text or image answers.
          </p>
        </div>
        <Button
          data-testid="admin-quiz-new"
          onClick={() => setEditing("new")}
          className="bg-blue-800 hover:bg-blue-700 text-white h-11"
        >
          <Plus size={15} className="mr-1.5" /> New quiz
        </Button>
      </div>

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}
      {!loading && Object.keys(grouped).length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500">
          No quizzes yet. Click <span className="font-semibold text-slate-700">New quiz</span> to build the first one.
        </div>
      )}

      {Object.entries(grouped).map(([key, list]) => {
        const [courseId, language] = key.split("::");
        return (
          <div key={key} className="rounded-xl border border-slate-200 bg-white">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  {language.toUpperCase()}
                </div>
                <div className="font-display font-semibold text-slate-900">
                  {courseTitle(courseId)}
                </div>
                {courseById(courseId) && courseById(courseId).language !== language && (
                  <div data-testid={`admin-quiz-lang-mismatch-${courseId}-${language}`} className="mt-1 text-xs font-semibold text-red-600">
                    ⚠ These {language.toUpperCase()} quizzes are attached to the {courseById(courseId).language?.toUpperCase()} course, so members won't see them. Open each one and pick the {language.toUpperCase()} course.
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-400 font-mono">{list.length} quizzes</div>
            </div>
            <div>
              {list.map((q) => (
                <div
                  key={q.id}
                  data-testid={`admin-quiz-row-${q.id}`}
                  className="flex items-center gap-4 px-5 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setEditing(q)}
                >
                  <div className="text-xs text-slate-400 font-mono w-8">
                    Ch{String(q.chapter_number).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{q.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {q.questions?.length || 0} questions · pass {q.passing_score}%
                      {q.questions?.some((qq) => qq.prompt_image || qq.options?.some((o) => o.image)) && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-700 font-semibold">
                          <ImageIcon size={11} /> images
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-blue-800"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`admin-quiz-delete-${q.id}`}
                    aria-label="Delete quiz"
                    title="Delete quiz — the chapter will have no quiz"
                    onClick={(e) => { e.stopPropagation(); deleteQuiz(q, load); }}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

async function deleteQuiz(q, onDone) {
  if (!window.confirm(`Delete "${q.title}"? Chapter ${q.chapter_number} will have no quiz — members can move on without one.`)) return;
  try {
    await api.delete(`/admin/quizzes/${q.id}`);
    toast.success("Quiz deleted.");
    onDone();
  } catch (e) {
    toast.error(e.response?.data?.detail || "Delete failed.");
  }
}

const LANG_LABEL = { en: "English", es: "Español", fr: "Français" };

function useCourseChapters(courseId) {
  const [chapters, setChapters] = useState([]);
  useEffect(() => {
    if (!courseId) { setChapters([]); return; }
    api.get("/admin/content/lessons", { params: { course_id: courseId } }).then(({ data }) => {
      const m = new Map();
      data.forEach((l) => {
        const n = l.chapter_number;
        if (n == null) return;
        if (!m.has(n)) m.set(n, { n, title: l.chapter_title || "", tier: l.chapter_tier || l.tier || "free", count: 0 });
        m.get(n).count += 1;
      });
      setChapters([...m.values()].sort((a, b) => a.n - b.n));
    }).catch(() => setChapters([]));
  }, [courseId]);
  return chapters;
}

// ─── Editor ────────────────────────────────────────────────────────────
function QuizEditor({ initial, courses, quizzes = [], onCancel, onSaved }) {
  const [form, setForm] = useState(() => initial || {
    title: "",
    course_id: "",
    chapter_number: 1,
    chapter_title: "",
    chapter_tier: "basic",
    language: "en",
    passing_score: 70,
    questions: [],
  });
  const [saving, setSaving] = useState(false);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const chapters = useCourseChapters(form.course_id);
  const selectedCourse = courses.find((c) => c.id === form.course_id);
  const langMismatch = selectedCourse && selectedCourse.language !== form.language;
  const pickCourse = (id) => {
    const c = courses.find((x) => x.id === id);
    setForm((f) => ({ ...f, course_id: id, language: c?.language || f.language, chapter_number: 1, chapter_title: "" }));
  };
  const pickChapter = (n) => {
    const ch = chapters.find((c) => c.n === n);
    setForm((f) => ({ ...f, chapter_number: n, chapter_title: ch?.title || f.chapter_title, chapter_tier: ch?.tier || f.chapter_tier }));
  };
  useEffect(() => {
    const ch = chapters.find((c) => c.n === form.chapter_number) || chapters[0];
    if (ch && !form.chapter_title) pickChapter(ch.n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters]);
  const chapterClash = quizzes.find((q) => q.id !== initial?.id && q.course_id === form.course_id && q.chapter_number === form.chapter_number);
  const coursesByLang = ["en", "es", "fr"].map((l) => [l, courses.filter((c) => c.language === l)]).filter(([, cs]) => cs.length);

  // ─ Question mutators ─
  const addQuestion = () => setForm((f) => ({
    ...f,
    questions: [...f.questions, {
      prompt: "",
      prompt_image: null,
      options: [{ text: "", image: null }, { text: "", image: null }],
      correct_index: 0,
      explanation: "",
    }],
  }));
  const removeQuestion = (i) => setForm((f) => ({
    ...f,
    questions: f.questions.filter((_, idx) => idx !== i),
  }));
  const moveQuestion = (i, dir) => setForm((f) => {
    const arr = [...f.questions];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, questions: arr };
  });
  const duplicateQuestion = (i) => setForm((f) => {
    const arr = [...f.questions];
    const copy = JSON.parse(JSON.stringify(arr[i]));
    arr.splice(i + 1, 0, copy);
    return { ...f, questions: arr };
  });
  const patchQuestion = (i, k, v) => setForm((f) => {
    const arr = [...f.questions];
    arr[i] = { ...arr[i], [k]: v };
    return { ...f, questions: arr };
  });
  const patchOption = (qi, oi, k, v) => setForm((f) => {
    const qs = [...f.questions];
    const opts = [...qs[qi].options];
    opts[oi] = { ...opts[oi], [k]: v };
    qs[qi] = { ...qs[qi], options: opts };
    return { ...f, questions: qs };
  });
  const addOption = (qi) => setForm((f) => {
    const qs = [...f.questions];
    qs[qi] = { ...qs[qi], options: [...qs[qi].options, { text: "", image: null }] };
    return { ...f, questions: qs };
  });
  const removeOption = (qi, oi) => setForm((f) => {
    const qs = [...f.questions];
    const opts = qs[qi].options.filter((_, idx) => idx !== oi);
    let ci = qs[qi].correct_index;
    if (oi === ci) ci = 0;
    else if (oi < ci) ci -= 1;
    qs[qi] = { ...qs[qi], options: opts, correct_index: Math.max(0, ci) };
    return { ...f, questions: qs };
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    if (!form.course_id) { toast.error("Pick a course."); return; }
    if (form.questions.length === 0) { toast.error("Add at least one question."); return; }
    // Validation: every question needs a prompt (or prompt image) + options
    for (const [i, q] of form.questions.entries()) {
      if (!q.prompt?.trim() && !q.prompt_image) {
        toast.error(`Question ${i + 1}: add a prompt or prompt image.`);
        return;
      }
      if (q.options.length < 2) {
        toast.error(`Question ${i + 1}: needs at least 2 options.`);
        return;
      }
      if (q.options.some((o) => !o.text?.trim() && !o.image)) {
        toast.error(`Question ${i + 1}: every option needs text or an image.`);
        return;
      }
    }

    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/quizzes/${initial.id}`, form);
        toast.success("Quiz updated.");
      } else {
        await api.post(`/admin/quizzes`, form);
        toast.success("Quiz created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-quiz-editor" className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={onCancel}
          className="text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1"
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      {/* Meta card */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Title</label>
            <Input
              value={form.title}
              onChange={(e) => patch("title", e.target.value)}
              data-testid="admin-quiz-title"
              placeholder="e.g. Chapter 1 · Market Basics"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Course</label>
            <select
              value={form.course_id}
              onChange={(e) => pickCourse(e.target.value)}
              data-testid="admin-quiz-course"
              className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 bg-white text-sm"
            >
              <option value="">— pick a course —</option>
              {coursesByLang.map(([l, cs]) => (
                <optgroup key={l} label={LANG_LABEL[l] || l.toUpperCase()}>
                  {cs.map((c) => <option key={c.id} value={c.id}>[{l.toUpperCase()}] {c.title}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Language</label>
            <div data-testid="admin-quiz-language" className="mt-1 h-10 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-700">
              {selectedCourse ? (LANG_LABEL[form.language] || form.language.toUpperCase()) : "Set by the course you pick"}
            </div>
            {langMismatch && (
              <div data-testid="admin-quiz-lang-warning" className="mt-1 text-xs font-semibold text-red-600">
                ⚠ This quiz was saved as {form.language.toUpperCase()} but the course is {selectedCourse.language.toUpperCase()}. Re-pick the course to fix it.
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Chapter</label>
            {chapters.length > 0 ? (
              <select
                value={form.chapter_number}
                onChange={(e) => pickChapter(parseInt(e.target.value))}
                data-testid="admin-quiz-chapter"
                className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 bg-white text-sm"
              >
                {!chapters.some((c) => c.n === form.chapter_number) && (
                  <option value={form.chapter_number}>Ch {form.chapter_number} — no lessons in this chapter</option>
                )}
                {chapters.map((c) => (
                  <option key={c.n} value={c.n}>Ch {c.n} · {c.title || "Untitled"} ({c.count} lesson{c.count === 1 ? "" : "s"})</option>
                ))}
              </select>
            ) : null}
            {chapterClash && (
              <div data-testid="admin-quiz-chapter-clash" className="mt-1 text-xs font-semibold text-amber-700">
                ⚠ This chapter already has a quiz ("{chapterClash.title}"). Edit that one instead, or pick another chapter.
              </div>
            )}
            {chapters.length === 0 && (
              <Input
                type="number" min="1"
                value={form.chapter_number}
                onChange={(e) => patch("chapter_number", parseInt(e.target.value) || 1)}
                data-testid="admin-quiz-chapter-number"
                className="mt-1"
              />
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Chapter title</label>
            <Input
              value={form.chapter_title}
              onChange={(e) => patch("chapter_title", e.target.value)}
              placeholder="e.g. Market Basics"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Chapter tier</label>
            <select
              value={form.chapter_tier}
              onChange={(e) => patch("chapter_tier", e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 bg-white text-sm"
            >
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Passing score (%)</label>
            <Input
              type="number" min="0" max="100"
              value={form.passing_score}
              onChange={(e) => patch("passing_score", parseInt(e.target.value) || 70)}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {form.questions.map((q, qi) => (
          <QuestionCard
            key={qi}
            q={q}
            index={qi}
            total={form.questions.length}
            onPrompt={(v) => patchQuestion(qi, "prompt", v)}
            onPromptImage={(v) => patchQuestion(qi, "prompt_image", v)}
            onCorrect={(v) => patchQuestion(qi, "correct_index", v)}
            onExplanation={(v) => patchQuestion(qi, "explanation", v)}
            onOptionText={(oi, v) => patchOption(qi, oi, "text", v)}
            onOptionImage={(oi, v) => patchOption(qi, oi, "image", v)}
            onAddOption={() => addOption(qi)}
            onRemoveOption={(oi) => removeOption(qi, oi)}
            onMove={(dir) => moveQuestion(qi, dir)}
            onDuplicate={() => duplicateQuestion(qi)}
            onRemove={() => removeQuestion(qi)}
          />
        ))}

        <Button
          onClick={addQuestion}
          variant="outline"
          data-testid="admin-quiz-add-question"
          className="w-full h-12 border-dashed border-slate-300 text-slate-600 hover:bg-blue-50/40 hover:text-blue-800"
        >
          <Plus size={15} className="mr-1.5" /> Add question
        </Button>
      </div>

      <div className="sticky bottom-4 z-10 flex items-center justify-end gap-3 bg-white/80 backdrop-blur p-3 rounded-xl border border-slate-200 shadow-sm">
        {initial?.id && (
          <Button
            variant="ghost"
            onClick={() => deleteQuiz(initial, onSaved)}
            data-testid="admin-quiz-delete"
            className="mr-auto text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 size={15} className="mr-1.5" /> Delete quiz
          </Button>
        )}
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="admin-quiz-save"
          className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6 font-semibold"
        >
          <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save quiz"}
        </Button>
      </div>
    </div>
  );
}

// ─── Question card ─────────────────────────────────────────────────────
function QuestionCard({
  q, index, total,
  onPrompt, onPromptImage, onCorrect, onExplanation,
  onOptionText, onOptionImage, onAddOption, onRemoveOption,
  onMove, onDuplicate, onRemove,
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
          Question {index + 1} of {total}
        </div>
        <div className="flex items-center gap-1">
          <IconBtn onClick={() => onMove(-1)} disabled={index === 0} icon={ChevronUp} label="Move up" />
          <IconBtn onClick={() => onMove(1)} disabled={index === total - 1} icon={ChevronDown} label="Move down" />
          <IconBtn onClick={onDuplicate} icon={Copy} label="Duplicate" />
          <IconBtn onClick={onRemove} icon={Trash2} label="Delete" danger />
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Prompt</label>
        <Textarea
          value={q.prompt}
          onChange={(e) => onPrompt(e.target.value)}
          data-testid={`admin-quiz-q${index}-prompt`}
          placeholder="e.g. What pattern is shown here?"
          rows={2}
          className="mt-1"
        />
      </div>

      <ImageField
        label="Prompt image (optional, rendered below the prompt)"
        value={q.prompt_image}
        onChange={onPromptImage}
        testId={`admin-quiz-q${index}-prompt-image`}
      />

      <div>
        <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2 block">
          Options — click the radio to mark the correct answer
        </label>
        <div className="space-y-2">
          {q.options.map((opt, oi) => (
            <div
              key={oi}
              className={`rounded-lg border p-3 flex flex-col sm:flex-row sm:items-start gap-3 ${
                q.correct_index === oi
                  ? "border-emerald-400 bg-emerald-50/50"
                  : "border-slate-200 bg-slate-50/50"
              }`}
            >
              <button
                type="button"
                onClick={() => onCorrect(oi)}
                data-testid={`admin-quiz-q${index}-o${oi}-correct`}
                className={`shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  q.correct_index === oi
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : "border-slate-300 hover:border-emerald-400"
                }`}
                title="Mark as correct answer"
              >
                {q.correct_index === oi && <span className="text-[11px]">✓</span>}
              </button>
              <div className="flex-1 min-w-0 space-y-2">
                <Input
                  value={opt.text}
                  onChange={(e) => onOptionText(oi, e.target.value)}
                  data-testid={`admin-quiz-q${index}-o${oi}-text`}
                  placeholder={`Option ${oi + 1}${opt.image ? " (caption — optional when image is set)" : ""}`}
                />
                <ImageField
                  label="Option image (optional)"
                  value={opt.image}
                  onChange={(v) => onOptionImage(oi, v)}
                  testId={`admin-quiz-q${index}-o${oi}-image`}
                  compact
                />
              </div>
              <button
                type="button"
                onClick={() => onRemoveOption(oi)}
                disabled={q.options.length <= 2}
                title="Remove option"
                className="shrink-0 text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <Button
          onClick={onAddOption}
          variant="outline"
          size="sm"
          data-testid={`admin-quiz-q${index}-add-option`}
          className="mt-2 border-dashed"
        >
          <Plus size={13} className="mr-1" /> Add option
        </Button>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
          Explanation (shown after submit)
        </label>
        <Textarea
          value={q.explanation}
          onChange={(e) => onExplanation(e.target.value)}
          placeholder="Why the correct answer is correct — helps students learn."
          rows={2}
          className="mt-1"
        />
      </div>
    </div>
  );
}

// ─── Small helpers ─────────────────────────────────────────────────────
function IconBtn({ onClick, disabled, icon: Icon, label, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`p-1.5 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        danger
          ? "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
          : "text-slate-500 hover:text-blue-800 hover:bg-blue-50"
      }`}
    >
      <Icon size={14} />
    </button>
  );
}

function ImageField({ label, value, onChange, testId, compact = false }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      {!compact && (
        <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
          {label}
        </label>
      )}
      {value ? (
        <div className="relative inline-block group">
          <img
            src={value}
            alt=""
            className="max-h-40 rounded-md border border-slate-200 object-contain bg-slate-50"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            title="Remove image"
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center shadow hover:bg-rose-500"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            data-testid={testId}
          />
          <Button
            type="button"
            variant="outline"
            size={compact ? "sm" : "default"}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="border-dashed text-slate-600"
          >
            <Upload size={13} className="mr-1.5" />
            {busy ? "Uploading…" : (compact ? "Add image" : "Upload image")}
          </Button>
        </div>
      )}
    </div>
  );
}
